import { removeLeadingSlash, removeTrailingSlash } from "@/lib/utils.ts"
import {
  ensureResolvedWebsiteUrl,
  ensureStartsWithMyAccountPath,
  getMyAccountPath,
  getPublicWebsiteUrl,
} from "@/lib/websiteUrls.ts"
import shouldServeNexus from "@/middleware/shouldServeNexus.ts"
import {
  isExternalRoute,
  isInternalRoute,
} from "@/plugins/router/routesHelper.ts"
import InAppBrowserService from "@/services/InAppBrowser.ts"
import { computed, MaybeRef, unref } from "vue"
import { Router } from "vue-router"

export default (
  initialTo: MaybeRef<string | undefined>,
  injectedRouter: Router,
) => {
  const allRoutes = injectedRouter.getRoutes()
  const to = computed(() => unref(initialTo))

  const isLiveRoute = computed(() => {
    // the legacy site doesn't have a trailing slash on the home route,
    // we just need to deal with that :(
    const routeForNexusCheck =
      to.value === "/"
        ? removeTrailingSlash(getMyAccountPath())
        : `${getMyAccountPath()}${removeLeadingSlash(to.value)}`

    return shouldServeNexus(
      routeForNexusCheck,
      location.hostname,
      location.hostname,
      import.meta.env,
    )
  })

  // legacy website URLS are opened in the in-app browser but also nexus routes
  // that are not yet live, or are feature flagged
  const isLegacyAppLink = computed(
    () =>
      isExternalRoute(to.value) ||
      (isInternalRoute(to.value, allRoutes) && !isLiveRoute.value),
  )

  // anything in the internal routes that is not explicitly disabled by the
  // shouldServeNexus function is rendered using a router-link
  const isInternalLink = computed(
    () => isInternalRoute(to.value, allRoutes) && isLiveRoute.value,
  )

  const isAnchorElement = computed(() => !isInternalLink.value)

  const correctedUrl = computed(() => {
    // an internal link which is not live should use the legacy my account url,
    // but will have been passed to us as Route.tariff for example and so will
    // not include the hostname or myaccount prefixes, we should add them
    if (isInternalRoute(to.value, allRoutes) && !isLiveRoute.value) {
      return `${getPublicWebsiteUrl()}${removeLeadingSlash(ensureStartsWithMyAccountPath(to.value))}`
    }

    return to.value
  })

  const linkAttributes = computed(() =>
    isAnchorElement.value ? { href: correctedUrl.value } : {},
  )

  const navigateToUrl = async (event: MouseEvent) => {
    if (!correctedUrl.value) return

    await InAppBrowserService.onLinkClicked(
      event,
      correctedUrl.value,
      isLegacyAppLink.value,
    )

    if (!event.defaultPrevented) {
      const normalizedUrl = ensureResolvedWebsiteUrl(to.value)
      window.location.assign(normalizedUrl)
    }
  }

  return {
    isAnchorElement,
    isLegacyAppLink,
    linkAttributes,
    correctedUrl,
    navigateToUrl,
  }
}
